/**
 * TheNewsPulse — a signed-in user's own submissions.
 * Route: /contribute/mine
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { npApi } from "./api";
import ContributorBadge from "@/components/newspulse/ContributorBadge";
import { Feather, Clock, CheckCircle2, XCircle, ExternalLink, Loader2 } from "lucide-react";

const STATUS_META = {
  pending_review: { icon: Clock,         cls: "text-amber-700 bg-amber-50 border-amber-200",  label: "Pending review" },
  approved:       { icon: CheckCircle2,  cls: "text-green-700 bg-green-50 border-green-200",  label: "Published" },
  rejected:       { icon: XCircle,       cls: "text-rose-700 bg-rose-50 border-rose-200",     label: "Not published" },
  withdrawn:      { icon: XCircle,       cls: "text-slate-700 bg-slate-100 border-slate-200", label: "Withdrawn" },
};

export default function NewsPulseMyContributions() {
  const { user, loading: authLoading } = useAuth() || {};
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user || authLoading) return;
    npApi.contribMine().then((d) => setRows(d.submissions || [])).catch((e) => setErr(e.message));
  }, [user, authLoading]);

  if (authLoading) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!user) {
    return (
      <div className="max-w-[720px] mx-auto px-4 py-16 text-center">
        <h1 className="text-lg md:text-lg font-bold mb-3">Sign in to view your contributions</h1>
        <Link to="/auth?next=/contribute/mine" className="text-blue-600 hover:text-blue-800 underline">
          Sign in with SocialPulse SSO
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-4 lg:px-6 py-10" data-testid="my-contributions-page">
      <div className="flex flex-wrap items-center justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700 mb-1.5">
            Your community reporting
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900"
              style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
            My Contributions
          </h1>
        </div>
        <Link to="/contribute/new"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-full transition-colors"
              data-testid="my-contributions-new-btn">
          <Feather className="w-4 h-4" /> Report a new story
        </Link>
      </div>

      {err && <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-900 mb-6">{err}</div>}

      <div className="mb-6">
        <ContributorBadge shape="widget" />
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading your submissions…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="my-contributions-empty">
          <p className="mb-4">You haven't reported any stories yet.</p>
          <Link to="/contribute/new" className="text-blue-600 hover:text-blue-800 underline">Start with one small story →</Link>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="my-contributions-list">
          {rows.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending_review;
            const loc = r.location || {};
            const locStr = [loc.neighborhood, loc.city, loc.state, loc.country].filter(Boolean).join(", ");
            return (
              <li key={r.id}
                  className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-blue-200 hover:shadow-sm transition-all"
                  data-testid={`my-contribution-${r.id}`}>
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <h3 className="text-base font-bold text-slate-900 flex-1">{r.headline}</h3>
                  <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${meta.cls}`}>
                    <meta.icon className="w-3 h-3" /> {meta.label}
                  </span>
                </div>
                <div className="text-[12px] text-slate-500 mb-2">
                  {locStr || "Location TBD"} · Submitted {(r.created_at || "").slice(0, 16).replace("T", " ")}
                  {r.published_event_id && (
                    <>
                      {" · "}
                      <Link to={`/event/${r.published_event_id}`}
                            className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-0.5">
                        View published <ExternalLink className="w-3 h-3" />
                      </Link>
                    </>
                  )}
                </div>
                <p className="text-sm text-slate-700 line-clamp-3">{r.story}</p>
                {r.status === "rejected" && r.reject_reason && (
                  <div className="mt-3 text-[12px] rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-900">
                    <strong>Editor note:</strong> {r.reject_reason}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
